/**
 * Data layer for `fraud-reports-most-fraudulent-campaigns-publishers`.
 * Typed fetch wrappers over /api/v1/fraud-reports-most-fraudulent-campaigns-publishers
 * ranked aggregations (by campaign, by publisher). Raw `fetch()` is confined
 * to this file per the dash contract (D6).
 */

export type FraudDim = "campaign" | "publisher" | "country";

export interface RankedRow {
	dim: FraudDim;
	key: string;
	fraud_count: number;
	click_count: number;
	conv_count: number;
	revenue_cents: number;
}

export interface RankedResponse {
	dim: FraudDim;
	limit: number;
	since: number | null;
	until: number | null;
	items: RankedRow[];
}

export interface RankedArgs {
	dim: FraudDim;
	limit?: number;
	since?: number | null;
	until?: number | null;
}

const BASE = "/api/v1/fraud-reports-most-fraudulent-campaigns-publishers";

function authHeaders(extra?: Record<string, string>): HeadersInit {
	return {
		accept: "application/json",
		"x-tenant-id": "t1",
		...(extra ?? {}),
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function fetchRanked(args: RankedArgs): Promise<RankedResponse> {
	const params = new URLSearchParams({ dim: args.dim });
	if (typeof args.limit === "number") params.set("limit", String(args.limit));
	if (args.since !== undefined && args.since !== null) {
		params.set("since", String(args.since));
	}
	if (args.until !== undefined && args.until !== null) {
		params.set("until", String(args.until));
	}
	const res = await fetch(`${BASE}/ranked?${params.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: authHeaders(),
	});
	return parseOrThrow<RankedResponse>(res);
}

export const fraudReportsMostFraudulentCampaignsPublishersQueryKeys = {
	all: ["fraud-reports-most-fraudulent-campaigns-publishers"] as const,
	ranked: (args: RankedArgs) =>
		[
			"fraud-reports-most-fraudulent-campaigns-publishers",
			"ranked",
			args.dim,
			args.limit ?? 10,
			args.since ?? null,
			args.until ?? null,
		] as const,
};
